/**
 * @class
 */
import Base from './assetConstituent';

import ProgressModel from '@netx/core-app/models/progress';

import {pad} from '@netx/core/lib/underscore-netx';

export default class AssetConstituent_Clip extends Base {
	/**
	 * @property {object}  defaults                   - The default attribute values
	 * @property {string}  defaults.endTime           - Clip end time string
	 * @property {string}  defaults.startTime         - Clip start time string
	 * @property {string}  defaults.title             - Default title
	 * @property {number}  defaults.type              - Default type (4 = clip)
	 *
	 * @property {boolean} defaults.keyframeReady     - Default keyframe ready state (client)
	 * @property {string}  defaults.uuid              - Default uuid for tracking creation/update progress
	 */
	get defaults() {
		return _.extend(super.defaults, {
			endTime: 0,
			startTime: 0,
			title: '',
			type: 4,
			// Client attrs
			keyframeReady: true,
			uuid: '',
		});
	}

	/** @type {array} */
	get clientAttrs() {
		return super.clientAttrs.concat(['keyframeReady', 'uuid']);
	}

	/**
	 * Right now, the only constituents that can be created by the client are clips
	 * @property {object}         methods          - Model RPC methods
	 * @property {function|array} methods.create   - Create clip
	 * @property {function|array} methods.remove   - Delete clip
	 * @property {function|array} methods.updated  - Update clip
	 */
	get methods() {
		return _.extend(super.methods, {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			create(opts) {
				return ['setClip', this.self()];
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			remove(opts) {
				return ['deleteClip', this.id];
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			update(opts) {
				return ['setClip', this.self()];
			},
			_prepareDownload: [
				'prepareDownload',
				'downloadOptions.assets',
				'downloadOptions.processes',
				'downloadOptions.arg1',
				'downloadOptions.arg2',
			],
		});
	}

	/**
	 * @property {object}   parsers         - Model parsers
	 * @property {function} parsers.create  - Parse the return of create clip
	 * @property {function} parsers.create  - Parse the return of update clip
	 */
	get parsers() {
		return _.extend(super.parsers, {
			/**
			 * @function
			 * @param {object} data Clip data (constituent and keyframe uuid)
			 * @param {object} opts Request options
			 */
			create(data, opts) {
				return _.extend(data.constituent, {
					keyframeReady: false,
					uuid: data.progressUuid,
				});
			},
			/**
			 * @function
			 * @param {object} data Clip data (constituent and [possibly] keyframe uuid)
			 * @param {object} opts Request options
			 */
			update(data) {
				let _data = {};
				if (data.progressUuid) {
					_data = {
						keyframeReady: false,
						uuid: data.progressUuid,
					};
				}
				return _.extend(data.constituent, _data);
			},
		});
	}

	/**
	 * @override
	 * @param {object} attr Model attributes
	 * @param {object} opts Model options
	 * @fires module:app#app:pollBatchJob
	 */
	constructor(attrs, opts = {}) {
		super(attrs, opts);

		/**
		 * @memberOf module:asset/models/assetConstituent_clip
		 * @alias downloadOptions
		 * @type {object}
		 */
		this.downloadOptions = opts.downloadOptions || {};

		/**
		 * @memberof module:asset/models/assetConstituent_clip
		 * @alias progress
		 * @type {module:app/models/progress}
		 */
		this.progress = new ProgressModel();

		// Hey, listen!
		// When a clip is beign created/updated on the server - track the progress
		this.listenTo(this, 'change:uuid', () => {
			this.trackBatchJob(this.get('uuid'));
		})
			.listenTo(this, 'progress', (model, batchJob, progress) => {
				const progressMessage = (batchJob && batchJob.get('details')) || '';
				this.progress.update(progress, progressMessage);
			})
			.listenTo(this, 'complete', (model, batchJob) => {
				this.fetch().then(() => {
					this.set({
						keyframeReady: true,
					});
					this.progress.complete();
				});
			});
	}

	/**
	 * return a cache-busting string for the asset preview; this string should change every time the asset preview changes.
	 * @method
	 * @return string
	 */
	cacheBuster(prefix) {
		// If this model doesn't have moddate set, use the current time.
		const timeStr = this.get('moddate')
			? _.dateFromAsset(this.get('moddate')).getTime()
			: _.now();

		return '?' + (prefix ? prefix + '&' : '') + 't=' + timeStr;
	}
	/**
	 * @method
	 * @return {object} Duration
	 */
	getDuration() {
		const start = parseFloat(this.get('startTime'));
		const end = parseFloat(this.get('endTime'));
		const floor = Math.floor;
		const duration = end - start;

		let hour, minute, second, millisecond, time;

		time = duration;
		hour = floor(time / 3600);
		time -= hour * 3600;
		minute = floor(time / 60);
		time -= floor(minute * 60);
		second = floor(time);
		millisecond = Math.round((time - second) * 1000);

		return {
			seconds: duration,
			pretty: [
				pad(hour, 2),
				':',
				pad(minute, 2),
				':',
				pad(second, 2),
				'.',
				pad(millisecond, 3),
			].join(''),
		};
	}
	/**
	 * Get the manual repurpose options for clip downloading
	 * @method
	 * @param {module:asset/models/assetProcess} process (Video) preset process data
	 * @returns {array} Manual repurpose options
	 */
	getManualRepurposeOptions(process) {
		return [
			{
				label: 'trim',
				value: [this.get('startTime'), this.get('endTime')].join('-'),
			},
			{label: 'codec', value: process.get('videoCodec')},
			{label: 'videoBitrate', value: process.get('videoBitrate')},
			{label: 'audioBitrate', value: process.get('audioBitrate')},
			{label: 'audioChannels', value: process.get('audioChannels')},
			{
				label: 'resize',
				value: [process.get('width'), process.get('height')].join(),
			},
			{label: 'format', value: process.get('format')},
		];
	}
	/**
	 * @method
	 * @return {object} Pretty range
	 */
	getPrettyRange() {
		const floor = Math.floor;
		const round = Math.round;
		const prettyRange = {};

		// Loops over start, end, duration
		_.each(['startTime', 'endTime'], (r) => {
			let hour, minute, second, millisecond, time;

			time = parseFloat(this.get(r)) || 0;
			hour = floor(time / 3600);
			time -= hour * 3600;
			minute = floor(time / 60);
			time -= floor(minute * 60);
			second = floor(time);
			millisecond = round((time - second) * 1000);

			prettyRange[r] = [
				pad(hour, 2),
				':',
				pad(minute, 2),
				':',
				pad(second, 2),
				'.',
				pad(millisecond, 3),
			].join('');
		});

		return prettyRange;
	}

	/** @type {string} */
	static get __name__() {
		return 'AssetConstituent_Clip';
	}
}
